import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
    MdPerson, MdEmail, MdLocationOn, 
    MdEdit, MdVerified, MdClose, 
    MdPeople, MdVisibility,
    MdBusiness, MdPhone, MdMoreVert, MdDelete,
    MdSettings, MdSchool, MdWork, MdCake 
} from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { 
    collection, query, where, getDocs, 
    orderBy, limit, doc, deleteDoc, updateDoc 
} from 'firebase/firestore'; 
import AppHeader from './AppHeader';

// --- RICH TEXT EDITOR FIX ---
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';

// ---------- Utility Functions ----------
const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*/g;

const getYouTubeId = (text) => {
    if (!text) return null;
    const match = new RegExp(youtubeRegex).exec(text);
    return match ? match[1] : null;
};

const renderTextWithoutURL = (text) => {
    if (!text) return "";
    return text.replace(youtubeRegex, "").trim();
};

// ---------- Styled Components ----------
const PageWrapper = styled.div`
    background: #f0f2f5;
    min-height: 100vh;
`;

const ViewProfileContainer = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 20px 15px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const CustomizerBox = styled.div`
  background: #fff; border-radius: 12px; padding: 15px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08); text-align: left;
  h4 { font-size: 14px; margin-bottom: 10px; display: flex; align-items: center; gap: 5px; }
  label { font-size: 11px; color: #5f6368; display: block; margin-bottom: 3px; }
  select { width: 100%; padding: 6px; border-radius: 6px; border: 1px solid #ddd; margin-bottom: 10px; outline: none; font-size: 12px; }
`;

const RichPostText = styled.div`
  font-size: ${(props) => props.$fsize || "14px"}; 
  font-family: ${(props) => props.$ffamily || "inherit"};
  line-height: 1.6; color: #1c1e21; margin: 10px 0;
  white-space: pre-wrap; word-break: break-word;
  b, strong { font-weight: bold; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }
  p { margin: 0; }
`;

const ProfileCard = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    overflow: hidden;
    position: relative;
    padding-bottom: 20px;
    ${props => props.$isStudent && `
        border: 2px solid #e3f2fd;
        background: linear-gradient(to bottom, #ffffff, #f1f8ff);
    `}
`;

const Banner = styled.div`
    height: 160px;
    background: ${props => props.$src ? `url(${props.$src})` : 'linear-gradient(135deg, #4A90E2 0%, #0d47a1 100%)'};
    background-size: cover;
    background-position: center;
    @media (min-width: 768px) { height: 280px; }
`;

const ProfileHeaderInfo = styled.div`
    padding: 0 20px;
    margin-top: -10px; 
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    @media (min-width: 768px) {
        padding: 0 40px;
        margin-top: -50px; 
        flex-direction: row;
        align-items: flex-end;
        text-align: left;
        gap: 25px;
    }
`;

const ProfileImg = styled.div`
    width: 110px; height: 110px;
    border-radius: 50%;
    border: 4px solid white;
    background: white;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    img { width: 100%; height: 100%; object-fit: cover; }
    @media (min-width: 768px) { width: 160px; height: 160px; }
`;

const RoleBadge = styled.span`
    background: #4A90E2;
    color: white;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
    vertical-align: middle;
    display: inline-block;
`;

const NameSection = styled.div`
    margin-top: 10px;
    flex: 1;
    font-family: ${(props) => props.$ffamily || "inherit"};
    h2 {
        font-size: 1.5rem; font-weight: 700; margin: 0; color: #1c1e21;
        display: flex; align-items: center; gap: 6px; justify-content: center;
        flex-wrap: wrap;
        @media (min-width: 768px) { font-size: 1.8rem; justify-content: flex-start; }
    }
    .username { color: #65676b; font-size: 14px; margin: 4px 0; }
    .meta-info {
        display: flex; flex-wrap: wrap; gap: 15px; margin-top: 10px;
        color: #65676b; font-size: ${(props) => props.$fsize || "13px"}; justify-content: center;
        span { display: flex; align-items: center; gap: 4px; }
        @media (min-width: 768px) { justify-content: flex-start; }
    }
`;

const ActionButtons = styled.div`
    display: flex; gap: 10px; margin-top: 15px;
    button {
        padding: 6px 14px; border-radius: 6px; font-weight: 600;
        cursor: pointer; border: 1px solid #ddd; display: flex;
        align-items: center; gap: 8px; font-size: 13px;
    }
    .edit-btn { background: white; color: #050505; &:hover { background: #f2f2f2; } }
`;

const InfoSection = styled.div`
    background: white; border-radius: 12px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-family: ${(props) => props.$ffamily || "inherit"};
    h3 { margin-top: 0; margin-bottom: 15px; font-size: 1.1rem; border-bottom: 1px solid #f0f2f5; padding-bottom: 10px; }
    .bio { color: #1c1e21; line-height: 1.6; margin-bottom: 20px; font-size: ${(props) => props.$fsize || "14px"}; white-wrap: pre-wrap; }
    .details-grid { display: grid; grid-template-columns: 1fr; gap: 15px; @media (min-width: 600px) { grid-template-columns: 1fr 1fr; } }
    .detail-item {
        display: flex; align-items: flex-start; gap: 10px; font-size: ${(props) => props.$fsize || "14px"}; color: #65676b;
        svg { color: #4A90E2; font-size: 18px; flex-shrink: 0; }
        b { color: #1c1e21; margin-right: 5px; }
    }
`;

const ConnectionsSection = styled.div`
    padding: 0 20px; margin-top: 20px;
    .conn-header { font-weight: 700; font-size: 14px; margin-bottom: 12px; display: flex; align-items: center; gap: 5px; }
    .conn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; }
`;

const ConnectionCard = styled.div`
    border: 1px solid #e0e0e0; border-radius: 8px; padding: 15px 10px;
    text-align: center; display: flex; flex-direction: column; align-items: center;
    .avatar { width: 60px; height: 60px; border-radius: 50%; margin-bottom: 8px; background: #f0f2f5; object-fit: cover; }
    .name { font-weight: 700; font-size: 13px; margin: 0; color: #1c1e21; }
    .job { font-size: 11px; color: #65676b; margin-bottom: 10px; }
    .view-btn { background: #e7f3ff; color: #1877f2; border: none; width: 100%; padding: 5px; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
`;

const PostsSection = styled.div`
    display: flex; flex-direction: column; gap: 15px;
    h3 { font-size: 1.1rem; margin-bottom: 5px; }
`;

const PostCard = styled.div`
    background: white; border-radius: 12px; padding: 15px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    position: relative;
    .post-header { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 12px; color: #65676b; align-items: center; }
    img, video { width: 100%; border-radius: 8px; margin-bottom: 10px; max-height: 500px; object-fit: contain; cursor: pointer; }
    .youtube-container { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 8px; iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; } }
`;

const OptionsButton = styled.div`
    cursor: pointer; position: relative;
    svg { font-size: 20px; color: #65676b; }
`;

const OptionsMenu = styled.div`
    position: absolute; right: 0; top: 25px; background: white; 
    border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); 
    width: 120px; z-index: 10; overflow: hidden;
    button {
        width: 100%; padding: 10px; border: none; background: none; 
        text-align: left; cursor: pointer; display: flex; align-items: center; gap: 8px;
        font-size: 13px; color: #1c1e21;
        &:hover { background: #f2f2f2; }
        &.delete { color: #d32f2f; }
    }
`;

const EditModal = styled.div`
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 3000;
    .modal-content {
        background: white; padding: 20px; border-radius: 12px; width: 95%; max-width: 500px;
        h3 { margin-bottom: 10px; }
        .quill-wrapper { margin: 15px 0; border: 1px solid #ddd; }
        .btns { display: flex; justify-content: flex-end; gap: 10px; margin-top: 15px;
            button { padding: 8px 16px; border-radius: 6px; cursor: pointer; }
        }
        .save-btn { background: #1877f2; color: white; border: none; }
        .cancel-btn { background: #e4e6eb; color: #050505; border: none; }
    }
`;

const FullScreenOverlay = styled.div`
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    background: rgba(0,0,0,0.9); display: flex; align-items: center; justify-content: center; z-index: 2000;
`;

// ---------- Main Component ----------
function ViewProfile() {
    const { fullName } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connections, setConnections] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [editingPost, setEditingPost] = useState(null);
    const [editValue, setEditValue] = useState("");

    // Font Customizer States
    const [userFontSize, setUserFontSize] = useState("14px");
    const [userFontStyle, setUserFontStyle] = useState("inherit");

    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ],
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            setLoading(true);
            try {
                const urlName = decodeURIComponent(fullName);
                const profilesRef = collection(db, "profiles");
                const querySnapshot = await getDocs(profilesRef);
                let foundUser = null;

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.fullName && data.fullName.replace(/\s+/g, '') === urlName) {
                        foundUser = { id: doc.id, ...data };
                    }
                });

                if (foundUser) {
                    setProfile(foundUser);
                    fetchPosts(foundUser.fullName);
                    fetchConnections(foundUser.uid);
                } else {
                    setProfile(null);
                }
            } catch (err) {
                console.error("Profile fetch error:", err);
            }
            setLoading(false);
        };

        fetchProfileData();
    }, [fullName]);

    const fetchPosts = async (authorFullName) => {
        try {
            const postsRef = collection(db, "posts");
            const q = query(postsRef, where("author", "==", authorFullName), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            const fetchedPosts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPosts(fetchedPosts);
        } catch (error) {
            console.error("Error fetching posts:", error);
        }
    };

    const fetchConnections = async (uid) => {
        if (!uid) return;
        try {
            const connRef = collection(db, "connections");
            const q = query(connRef, where("userUid", "==", uid), limit(6));
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(doc => doc.data());
            setConnections(list);
        } catch (e) {
            console.error("Connections fetch error:", e);
        }
    };

    const handleDeletePost = async (postId) => {
        if (window.confirm("Are you sure you want to delete this post?")) {
            try {
                await deleteDoc(doc(db, "posts", postId));
                setPosts(posts.filter(p => p.id !== postId));
                setActiveMenu(null);
            } catch (err) {
                console.error("Delete failed:", err);
            }
        }
    };

    const handleEditPostClick = (post) => {
        setEditingPost(post);
        setEditValue(post.text || "");
        setActiveMenu(null);
    };

    const handleUpdatePost = async () => {
        if (!editingPost) return;
        try {
            const postDocRef = doc(db, "posts", editingPost.id);
            await updateDoc(postDocRef, { text: editValue });
            setPosts(posts.map(p => p.id === editingPost.id ? { ...p, text: editValue } : p));
            setEditingPost(null);
        } catch (err) {
            console.error("Update failed:", err);
        }
    };

    if (loading) {
        return (
            <PageWrapper>
                <AppHeader />
                <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px' }}>Loading profile...</div>
            </PageWrapper>
        );
    }

    if (!profile) {
        return (
            <PageWrapper>
                <AppHeader />
                <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px', color: 'red' }}>Profile not found!</div>
            </PageWrapper>
        );
    }

    const mainRoleLower = profile.mainRole ? profile.mainRole.toLowerCase() : '';
    const isStudent = mainRoleLower === 'student';
    const isCurrentUser = auth.currentUser && auth.currentUser.uid === profile.uid;

    return (
        <HelmetProvider>
            <PageWrapper>
                <Helmet>
                    <title>{profile.fullName || "User Profile"}</title>
                </Helmet>
                <AppHeader />
                <ViewProfileContainer>
                    {/* Font Customizer */}
                    <CustomizerBox>
                        <h4><MdSettings /> Customize View</h4>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label>Font Size</label>
                                <select value={userFontSize} onChange={(e) => setUserFontSize(e.target.value)}>
                                    <option value="13px">Small</option>
                                    <option value="15px">Medium</option>
                                    <option value="18px">Large</option>
                                    <option value="22px">Extra Large</option>
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label>Font Family</label>
                                <select value={userFontStyle} onChange={(e) => setUserFontStyle(e.target.value)}>
                                    <option value="inherit">Default</option>
                                    <option value="'Courier New', Courier, monospace">Monospace</option>
                                    <option value="'Georgia', serif">Serif</option>
                                    <option value="'Lucida Sans', sans-serif">Lucida</option>
                                </select>
                            </div>
                        </div>
                    </CustomizerBox>

                    {/* Main Profile Card */}
                    <ProfileCard $isStudent={isStudent}>
                        <Banner $src={profile.coverImageUrl} />
                        <ProfileHeaderInfo>
                            <ProfileImg>
                                <img src={profile.profileImageUrl} alt="Profile" />
                            </ProfileImg>
                            <NameSection $ffamily={userFontStyle} $fsize={userFontSize}>
                                <h2>
                                    {profile.fullName}
                                    {profile.mainRole && <RoleBadge>{profile.mainRole}</RoleBadge>}
                                </h2>
                                <p className="username">@{profile.fullName ? profile.fullName.toLowerCase().replace(/\s+/g, '') : 'user'}</p>
                                <div className="meta-info">
                                    {profile.city && <span><MdLocationOn /> {profile.city}, {profile.state}</span>}
                                    {profile.country && <span>{profile.country}</span>}
                                </div>
                                {isCurrentUser && (
                                    <ActionButtons>
                                        <button className="edit-btn" onClick={() => navigate('/edit-profile')}><MdEdit /> Edit Profile</button>
                                    </ActionButtons>
                                )}
                            </NameSection>
                        </ProfileHeaderInfo>

                        {connections.length > 0 && (
                            <ConnectionsSection>
                                <div className="conn-header"><MdPeople /> Connections ({connections.length})</div>
                                <div className="conn-grid">
                                    {connections.map((c, idx) => (
                                        <ConnectionCard key={idx}>
                                            <img className="avatar" src={c.targetImage || "/bdp.jpg"} alt="" />
                                            <p className="name">{c.targetName}</p>
                                            <p className="job">{c.targetRole || "Member"}</p>
                                            <button className="view-btn" onClick={() => navigate(`/profile/${c.targetName.replace(/\s+/g, '')}`)}>View</button>
                                        </ConnectionCard>
                                    ))}
                                </div>
                            </ConnectionsSection>
                        )}
                    </ProfileCard>

                    {/* Information Section based on create/edit profile fields */}
                    <InfoSection $ffamily={userFontStyle} $fsize={userFontSize}>
                        <h3>About & Professional Details</h3>
                        
                        {/* Dynamic displays according to specific mainRole */}
                        {mainRoleLower === 'business owner' && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-item" style={{ marginBottom: '10px' }}>
                                    <MdBusiness />
                                    <div><b>Business Name:</b> {profile.businessName || 'N/A'}</div>
                                </div>
                                <div className="detail-item">
                                    <MdWork />
                                    <div><b>Industry:</b> {profile.businessIndustry || 'N/A'}</div>
                                </div>
                                <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '15px 0' }} />
                            </div>
                        )}

                        {mainRoleLower === 'manager' && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-item" style={{ marginBottom: '10px' }}>
                                    <MdBusiness />
                                    <div><b>Company Name:</b> {profile.managerCompany || 'N/A'}</div>
                                </div>
                                <div className="detail-item">
                                    <MdWork />
                                    <div><b>Department:</b> {profile.managerDepartment || 'N/A'}</div>
                                </div>
                                <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '15px 0' }} />
                            </div>
                        )}

                        {mainRoleLower === 'employee' && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-item" style={{ marginBottom: '10px' }}>
                                    <MdBusiness />
                                    <div><b>Organization:</b> {profile.employeeCompany || 'N/A'}</div>
                                </div>
                                <div className="detail-item">
                                    <MdWork />
                                    <div><b>Job Title / Designation:</b> {profile.employeeDesignation || 'N/A'}</div>
                                </div>
                                <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '15px 0' }} />
                            </div>
                        )}

                        {mainRoleLower === 'student' && (
                            <div style={{ marginBottom: '20px' }}>
                                <div className="detail-item" style={{ marginBottom: '10px' }}>
                                    <MdSchool />
                                    <div><b>Institution Name:</b> {profile.studentInstitution || 'N/A'}</div>
                                </div>
                                <div className="detail-item">
                                    <MdSchool />
                                    <div><b>Course / Standard:</b> {profile.studentCourse || 'N/A'}</div>
                                </div>
                                <hr style={{ border: 'none', borderTop: '1px solid #f0f2f5', margin: '15px 0' }} />
                            </div>
                        )}

                        {/* Common Profile Information Fields */}
                        <div className="details-grid">
                            <div className="detail-item">
                                <MdPerson />
                                <div><b>Gender:</b> {profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : 'N/A'}</div>
                            </div>
                            {profile.dateOfBirth && (
                                <div className="detail-item">
                                    <MdCake />
                                    <div><b>Date of Birth:</b> {profile.dateOfBirth}</div>
                                </div>
                            )}
                            <div className="detail-item">
                                <MdPhone />
                                <div><b>Phone Number:</b> {profile.phone || 'N/A'}</div>
                            </div>
                            <div className="detail-item">
                                <MdLocationOn />
                                <div><b>Address:</b> {profile.address || 'N/A'}</div>
                            </div>
                            <div className="detail-item">
                                <MdLocationOn />
                                <div><b>City & Pincode:</b> {profile.city || 'N/A'}{profile.pincode ? ` - ${profile.pincode}` : ''}</div>
                            </div>
                            <div className="detail-item">
                                <MdLocationOn />
                                <div><b>State & Country:</b> {profile.state || 'N/A'}, {profile.country || 'N/A'}</div>
                            </div>
                        </div>
                    </InfoSection>

                    {/* Posts Display Section */}
                    <PostsSection>
                        <h3>Posts by {profile.fullName}</h3>
                        {posts.length === 0 ? (
                            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', color: '#65676b' }}>No posts yet.</div>
                        ) : (
                            posts.map((post) => {
                                const ytid = getYouTubeId(post.text);
                                return (
                                    <PostCard key={post.id}>
                                        <div className="post-header">
                                            <span>Posted on {post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}</span>
                                            {isCurrentUser && (
                                                <OptionsButton onClick={() => setActiveMenu(activeMenu === post.id ? null : post.id)}>
                                                    <MdMoreVert />
                                                    {activeMenu === post.id && (
                                                        <OptionsMenu onClick={e => e.stopPropagation()}>
                                                            <button onClick={() => handleEditPostClick(post)}><MdEdit /> Edit</button>
                                                            <button className="delete" onClick={() => handleDeletePost(post.id)}><MdDelete /> Delete</button>
                                                        </OptionsMenu>
                                                    )}
                                                </OptionsButton>
                                            )}
                                        </div>

                                        {post.text && (
                                            <RichPostText $fsize={userFontSize} $ffamily={userFontStyle} dangerouslySetInnerHTML={{ __html: renderTextWithoutURL(post.text) }} />
                                        )}

                                        {ytid && (
                                            <div className="youtube-container" style={{ marginBottom: '10px' }}>
                                                <iframe src={`https://www.youtube.com/embed/${ytid}`} title="ytvideo" frameBorder="0" allowFullScreen></iframe>
                                            </div>
                                        )}

                                        {post.mediaUrl && (
                                            post.mediaUrl.includes('mp4') ? 
                                                <video src={post.mediaUrl} controls onClick={() => setSelectedPost(post)}/> : 
                                                <img src={post.mediaUrl} alt="Post asset" onClick={() => setSelectedPost(post)}/>
                                        )}
                                    </PostCard>
                                );
                            })
                        )}
                    </PostsSection>
                </ViewProfileContainer>

                {/* Edit Post Modal */}
                {editingPost && (
                    <EditModal>
                        <div className="modal-content">
                            <h3>Edit Post</h3>
                            <div className="quill-wrapper">
                                <ReactQuill 
                                    theme="snow" 
                                    value={editValue} 
                                    onChange={setEditValue} 
                                    modules={quillModules}
                                />
                            </div>
                            <div className="btns">
                                <button className="cancel-btn" onClick={() => setEditingPost(null)}>Cancel</button>
                                <button className="save-btn" onClick={handleUpdatePost}>Save Changes</button>
                            </div>
                        </div>
                    </EditModal>
                )}

                {/* Full Screen Image/Video View */}
                {selectedPost && (
                    <FullScreenOverlay onClick={() => setSelectedPost(null)}>
                        <div style={{width: '95%', maxWidth: '1000px', position: 'relative'}} onClick={e => e.stopPropagation()}>
                            <MdClose style={{position: 'absolute', top: '-40px', right: 0, color: 'white', cursor: 'pointer', fontSize: '30px'}} onClick={() => setSelectedPost(null)} />
                            {selectedPost.mediaUrl.includes('mp4') ? 
                                <video src={selectedPost.mediaUrl} controls autoPlay style={{width:'100%', borderRadius:'8px'}}/> : 
                                <img src={selectedPost.mediaUrl} style={{width:'100%', borderRadius:'8px'}} alt="preview"/>
                            }
                        </div>
                    </FullScreenOverlay>
                )}
            </PageWrapper>
        </HelmetProvider>
    );
}

export default ViewProfile;